import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Select,
  Input,
  Flex,
  Spinner,
  Text,
  Tag,
  useToast,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";
import axios from "axios";

const PAGE_SIZE = 20;
const statusOptions = ["Pending", "Approved", "Rejected"];

const Channels = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [inputPage, setInputPage] = useState("1");
  const [statusFilter, setStatusFilter] = useState("Pending");

  // 데이터 불러오기
  const fetchData = async () => {
    setLoading(true);
    let query = supabase
      .from("shorts_channel")
      .select("*", { count: "exact" })
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (search) {
      query = query.or(
        `channel_title.ilike.%${search}%,channel_id.ilike.%${search}%,user.ilike.%${search}%`
      );
    }
    if (statusFilter && statusFilter !== "All") {
      query = query.eq("status", statusFilter);
    }

    const { data: rows, count, error } = await query;
    if (!error) {
      setData(rows || []);
      setTotalPages(Math.max(1, Math.ceil((count || 1) / PAGE_SIZE)));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [search, page, statusFilter]);

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  // status 변경
  const handleStatusChange = async (id, newStatus) => {
    const { error } = await supabase
      .from("shorts_channel")
      .update({ status: newStatus })
      .eq("id", id);
    if (!error) {
      toast({ title: "상태가 변경되었습니다.", status: "success" });
      fetchData();
    } else {
      toast({
        title: "상태 변경 실패",
        status: "error",
        description: error.message,
      });
    }
  };

  // 날짜를 'YYYY-MM-DD HH:MM:SS'로 변환하는 함수
  function toDatetimeString(date) {
    if (!date) return null;
    const d = typeof date === "string" ? new Date(date) : date;
    const pad = (n) => n.toString().padStart(2, "0");
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      " " +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes()) +
      ":" +
      pad(d.getSeconds())
    );
  }

  // LocalDateTime용 ISO 문자열 변환 함수 (소수점 이하, Z 포함)
  function toIsoDatetimeString(date) {
    if (!date) return null;
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toISOString();
  }

  // 승인 처리 (외부 API 연동)
  const handleApprove = async (row) => {
    try {
      const { data: userData } = await supabase
        .from("user")
        .select("id")
        .eq("email", row.user);
      const user = userData[0];

      // 1. access_token 발급
      const response = await fetch(`https://plpl.rhoonart.co.kr/auth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId: user.id }),
      });

      const result = await response.json();
      const access_token = result.data?.access_token;

      if (!access_token) throw new Error("토큰 발급 실패");

      // 2. 채널 정보 전송
      const body = {
        id: row.id.toString() ?? "",
        channelId: row.channel_id?.toString() ?? "",
        channelTitle: row.channel_title?.toString() ?? "",
        channelUrl: row.channel_url?.toString() ?? "",
        description: row.description ?? "",
        userId: user.id?.toString() ?? "",
        subscriberCount:
          row.subscriber_count !== undefined && row.subscriber_count !== null
            ? Number(row.subscriber_count)
            : 0,
        videoCount:
          row.video_count !== undefined && row.video_count !== null
            ? Number(row.video_count)
            : 0,
        viewCount:
          row.view_count !== undefined && row.view_count !== null
            ? Number(row.view_count)
            : 0,
        partnerId: row.partner ? row.partner.toString() : null,
        status: "Approved",
        thumbnails: row.thumbnails?.toString() ?? "",
        registerAt: row.registered_at
          ? toIsoDatetimeString(row.registered_at)
          : null,
        creatorId: user.id?.toString() ? user.id?.toString() : null,
        createAt: row.created_date
          ? toIsoDatetimeString(row.created_date)
          : null,
        updateAt: row.modified_date
          ? toIsoDatetimeString(row.modified_date)
          : null,
      };

      console.log("Request body:", body); // 디버깅을 위한 로그 추가

      const channelResponse = await axios.post(
        "https://plpl.rhoonart.co.kr/channels",
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access_token}`,
          },
        }
      );

      console.log("Response:", channelResponse.data); // 응답 확인을 위한 로그 추가

      // 3. supabase status도 Approved로 변경
      await handleStatusChange(row.id, "Approved");
    } catch (e) {
      toast({
        title: "승인 처리 실패",
        description: e.message,
        status: "error",
      });
    }
  };

  // 페이지 직접 입력
  const handlePageInput = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) setInputPage(value);
  };
  const handlePageGo = () => {
    const num = parseInt(inputPage, 10);
    if (num >= 1 && num <= totalPages) setPage(num);
    else
      toast({ title: "올바른 페이지 번호를 입력하세요.", status: "warning" });
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
    setInputPage("1");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box maxW="1200px" mx="auto" py={8}>
      <Flex mb={4} gap={2} align="center">
        <Select
          width="120px"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="Pending">대기</option>
          <option value="Approved">승인</option>
          <option value="Rejected">거절</option>
        </Select>
        <Input
          placeholder="채널명, 채널ID, 사용자 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleKeyPress}
          width="300px"
        />
        <Button colorScheme="blue" onClick={handleSearch}>
          검색
        </Button>
      </Flex>
      <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="md">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>채널</Th>
              <Th>유저</Th>
              <Th>구독자</Th>
              <Th>영상수</Th>
              <Th>조회수</Th>
              <Th>상태</Th>
              <Th>관리</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={7}>
                  <Spinner />
                </Td>
              </Tr>
            ) : data.length === 0 ? (
              <Tr>
                <Td colSpan={7}>
                  <Text textAlign="center" color="gray.400">
                    데이터 없음
                  </Text>
                </Td>
              </Tr>
            ) : (
              data.map((row) => {
                // 상태 한글 변환
                let statusText = row.status;
                if (row.status === "Pending") statusText = "대기";
                else if (row.status === "Approved") statusText = "승인";
                else if (row.status === "Rejected") statusText = "거절";
                return (
                  <Tr key={row.id}>
                    <Td>
                      <a
                        href={row.channel_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "none",
                        }}
                      >
                        <Flex align="center" gap={3}>
                          {row.thumbnails && (
                            <img
                              src={row.thumbnails}
                              alt="thumb"
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                objectFit: "cover",
                              }}
                            />
                          )}
                          <Box>
                            <Text fontWeight="bold">{row.channel_title}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {row.channel_id}
                            </Text>
                          </Box>
                        </Flex>
                      </a>
                    </Td>
                    <Td>{row.user}</Td>
                    <Td>{row.subscriber_count?.toLocaleString?.() ?? "-"}</Td>
                    <Td>{row.video_count ?? "-"}</Td>
                    <Td>{row.view_count?.toLocaleString?.() ?? "-"}</Td>
                    <Td>
                      <Tag
                        colorScheme={
                          row.status === "Approved"
                            ? "green"
                            : row.status === "Rejected"
                            ? "red"
                            : row.status === "Pending"
                            ? "blue"
                            : row.status === "Active"
                            ? "green"
                            : row.status === "Inactive"
                            ? "gray"
                            : "gray"
                        }
                      >
                        {statusText}
                      </Tag>
                    </Td>
                    <Td>
                      {row.status === "Pending" ? (
                        <Flex gap={2}>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => handleApprove(row)}
                          >
                            승인
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            onClick={() =>
                              handleStatusChange(row.id, "Rejected")
                            }
                          >
                            거절
                          </Button>
                        </Flex>
                      ) : row.status === "Approved" ? (
                        <Flex gap={2}>
                          <Button size="sm" colorScheme="blue" isDisabled>
                            승인
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            onClick={() =>
                              handleStatusChange(row.id, "Rejected")
                            }
                          >
                            거절
                          </Button>
                        </Flex>
                      ) : row.status === "Rejected" ? (
                        <Flex gap={2}>
                          <Button size="sm" colorScheme="blue" isDisabled>
                            승인
                          </Button>
                          <Button size="sm" colorScheme="red" isDisabled>
                            거절
                          </Button>
                        </Flex>
                      ) : (
                        <Select
                          size="sm"
                          value={row.status}
                          onChange={(e) =>
                            handleStatusChange(row.id, e.target.value)
                          }
                          width="100px"
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>
      {/* 페이지네이션 */}
      <Flex justify="center" mt={4} gap={2} align="center">
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          이전
        </Button>
        <Input
          value={inputPage}
          onChange={handlePageInput}
          onBlur={handlePageGo}
          size="sm"
          width="50px"
          textAlign="center"
          fontSize="sm"
          placeholder="페이지"
        />
        <Button size="sm" onClick={handlePageGo}>
          이동
        </Button>
        <Text fontSize="sm" px={2}>
          / {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          다음
        </Button>
      </Flex>
    </Box>
  );
};

export default Channels;
